'use client';

/**
 * ProductDetailPanel
 *
 * Sticky sidebar panel for individual product model pages.
 * Surfaces: part number, specifications table, datasheet download,
 * stock status, and a Request-a-Quote CTA that opens the modal form.
 *
 * Data shape expected (all optional — component renders only what is present):
 *   productData.subtitle      → part number / model code
 *   productData.price         → "In Stock" | "On Request" | any string
 *   productData.product_type  → alternate stock status label
 *   specifications.list[]     → [{ item_title, item_value }]
 *   files.list[]              → [{ short_title, full_path, img_name }]
 *   onRequestQuote()          → callback to open the modal
 */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import ButtonNormal from '@/components/common/ui/buttons/ButtonNormal';
import { theme } from '@/components/features/theme/theme';
import reactHtmlParser from 'react-html-parser';

/* ─── Stock-status helpers ──────────────────────────────────────────────── */

const STOCK_LABELS = {
	'in stock': { label: 'In Stock', color: '#1a7f37' },
	instock: { label: 'In Stock', color: '#1a7f37' },
	available: { label: 'In Stock', color: '#1a7f37' },
	'on request': { label: 'On Request', color: '#d97706' },
	'made to order': { label: 'Made to Order', color: '#d97706' },
	'out of stock': { label: 'Out of Stock', color: '#c0392b' },
};

function resolveStockStatus(price, productType) {
	const value = price || productType;
	if (!value) return null;
	
	const raw = String(value).toLowerCase().trim();
	if (!raw) return null;
	
	return STOCK_LABELS[raw] || { label: String(value), color: '#152637' };
}

/* ─── File-type icon ────────────────────────────────────────────────────── */

function fileIcon(name = '') {
	const ext = name.split('.').pop().toLowerCase();
	if (ext === 'pdf') return '📄';
	if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
	if (['zip', 'rar'].includes(ext)) return '🗜️';
	return '📎';
}

/* ─── Component ─────────────────────────────────────────────────────────── */

const ProductDetailPanel = ({ productData = {}, specifications = [], files = [], onRequestQuote }) => {
	const partNumber = productData?.subtitle?.trim();
	const stockStatus = useMemo(
		() => resolveStockStatus(productData?.price, productData?.product_type),
		[productData?.price, productData?.product_type],
	);

	const datasheetFiles = useMemo(
		() =>
			files.filter(f => {
				const name = (f?.short_title || f?.img_name || '').toLowerCase();
				const path = (f?.full_path || '').toLowerCase();
				return (
					name.includes('datasheet') ||
					name.includes('catalog') ||
					name.includes('brochure') ||
					name.includes('spec') ||
					path.endsWith('.pdf')
				);
			}),
		[files],
	);

	const hasContent =
		partNumber || stockStatus || specifications.length > 0 || datasheetFiles.length > 0;

	if (!hasContent && !onRequestQuote) return null;

	return (
		<StyledPanel className="product-detail-panel">
			{/* ── Part Number ────────────────────────────────────────────── */}
			{partNumber && (
				<div className="panel-row panel-part-number">
					<span className="label">Part Number</span>
					<span className="value mono">{reactHtmlParser(partNumber)}</span>
				</div>
			)}

			{/* ── Stock Status ───────────────────────────────────────────── */}
			{stockStatus && (
				<div className="panel-row panel-stock">
					<span className="label">Availability</span>
					<span
						className="badge-status"
						style={{ backgroundColor: stockStatus.color + '18', color: stockStatus.color, border: `1px solid ${stockStatus.color}40` }}>
						<span className="dot" style={{ background: stockStatus.color }} />
						{stockStatus.label}
					</span>
				</div>
			)}

			{/* ── Specifications ─────────────────────────────────────────── */}
			{specifications.length > 0 && (
				<div className="panel-section">
					<h3 className="panel-section-title">Specifications</h3>
					<table className="spec-table" aria-label="Product specifications">
						<tbody>
							{specifications.map((spec, i) => (
								<tr key={i}>
									<th scope="row">{reactHtmlParser(spec?.item_title || spec?.title || '')}</th>
									<td>{reactHtmlParser(spec?.item_value || spec?.value || '')}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* ── Datasheet / Files ──────────────────────────────────────── */}
			{datasheetFiles.length > 0 && (
				<div className="panel-section">
					<h3 className="panel-section-title">Downloads</h3>
					<ul className="file-list" aria-label="Product downloads">
						{datasheetFiles.map((f, i) => {
							const label = f?.short_title || f?.img_name || 'Download';
							return (
								<li key={i}>
									<Link
										href={f?.full_path || '#'}
										target="_blank"
										rel="noopener noreferrer"
										aria-label={`Download ${label}`}
										download>
										<span className="file-icon" aria-hidden="true">
											{fileIcon(f?.full_path || f?.img_name || '')}
										</span>
										<span className="file-name">{label}</span>
										<span className="file-arrow" aria-hidden="true">↓</span>
									</Link>
								</li>
							);
						})}
					</ul>
				</div>
			)}

			{/* ── CTAs ───────────────────────────────────────────────────── */}
			{onRequestQuote && (
				<div className="panel-ctas">
					<ButtonNormal
						hoverBackground="#152637"
						text="Request a Quote"
						src="#get-a-quote"
						onClick={e => {
							e.preventDefault();
							window.history.pushState(null, '', '#get-a-quote');
							onRequestQuote();
						}}
						background="#DD223F"
						color="#FFFFFF"
						width="100%"
					/>
					<ButtonNormal
						hoverBackground="#DD223F"
						text="Request a Demo"
						src="#get-a-demo"
						onClick={e => {
							e.preventDefault();
							window.history.pushState(null, '', '#get-a-demo');
							onRequestQuote();
						}}
						background="transparent"
						color="#152637"
						borderColor="#152637"
						width="100%"
						margin="12px 0 0"
					/>
				</div>
			)}
		</StyledPanel>
	);
};

/* ─── Styles ─────────────────────────────────────────────────────────────── */

const StyledPanel = styled.aside`
	background: #f8f8f8;
	border: 1px solid rgba(21, 38, 55, 0.1);
	border-radius: 8px;
	padding: 28px 24px;
	margin-top: 30px;
	font-size: 0.9375rem;

	.panel-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 10px 0;
		border-bottom: 1px solid rgba(21, 38, 55, 0.08);

		&:last-of-type {
			border-bottom: none;
		}

		.label {
			font-weight: 600;
			color: rgba(21, 38, 55, 0.6);
			font-size: 0.8125rem;
			text-transform: uppercase;
			letter-spacing: 0.05em;
			flex-shrink: 0;
		}

		.value {
			color: #152637;
			font-weight: 500;
			text-align: right;
		}

		.mono {
			font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
			font-size: 0.875rem;
			background: #152637;
			color: #fff;
			padding: 2px 8px;
			border-radius: 4px;
		}
	}

	.badge-status {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 3px 10px;
		border-radius: 100px;
		font-size: 0.8125rem;
		font-weight: 600;

		.dot {
			width: 7px;
			height: 7px;
			border-radius: 50%;
			flex-shrink: 0;
		}
	}

	.panel-section {
		margin-top: 24px;

		.panel-section-title {
			font-size: 0.8125rem;
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 0.05em;
			color: rgba(21, 38, 55, 0.5);
			margin-bottom: 12px;
		}
	}

	.spec-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.875rem;

		tr {
			border-bottom: 1px solid rgba(21, 38, 55, 0.06);

			&:last-child {
				border-bottom: none;
			}
		}

		th {
			text-align: left;
			font-weight: 500;
			color: rgba(21, 38, 55, 0.65);
			padding: 8px 12px 8px 0;
			width: 45%;
			vertical-align: top;
		}

		td {
			color: #152637;
			font-weight: 500;
			padding: 8px 0;
			vertical-align: top;
		}
	}

	.file-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 8px;

		li a {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 10px 12px;
			background: #fff;
			border: 1px solid rgba(21, 38, 55, 0.12);
			border-radius: 6px;
			color: #152637;
			font-weight: 500;
			font-size: 0.875rem;
			transition: ${theme.transition};
			text-decoration: none;

			&:hover {
				border-color: #DD223F;
				color: #DD223F;
			}

			.file-icon {
				font-size: 1rem;
				flex-shrink: 0;
			}

			.file-name {
				flex: 1;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			.file-arrow {
				margin-left: auto;
				font-size: 0.75rem;
				opacity: 0.6;
				flex-shrink: 0;
			}
		}
	}

	.panel-ctas {
		margin-top: 24px;
	}

	@media (max-width: 767px) {
		padding: 20px 16px;
		margin-top: 24px;
	}
`;

export default React.memo(ProductDetailPanel);
